import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { Session } from "@insite/client-framework/Services/SessionService";
import { QuoteModel, QuoteSettingsModel } from "@insite/client-framework/Types/ApiModels";
import RfqQuoteDetailsPageTypeLink from "@insite/content-library/Components/RfqQuoteDetailsPageTypeLink";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getLocalizedDate from "@rightstore/Utilities/getLocalizedDate";
import { translatedStatus } from "@rightstore/Utilities/quoteStatusFormatter";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    quote: QuoteModel;
    session: Session;
    quoteSettings: QuoteSettingsModel;
    extendedStyles?: QuoteSummaryCardStyles;
}

type Props = OwnProps;

export interface QuoteSummaryCardStyles {
    quoteSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    quoteDetailsLinkGridItem?: GridItemProps;
    typeGridItem?: GridItemProps;
    typeHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    orderDateGridItem?: GridItemProps;
    orderDateHeadingAndText?: SmallHeadingAndTextStyles;
    expirationDateGridItem?: GridItemProps;
    expirationDateHeadingAndText?: SmallHeadingAndTextStyles;
    salesRepGridItem?: GridItemProps;
    salesRepHeadingAndText?: SmallHeadingAndTextStyles;
    userNameGridItem?: GridItemProps;
    userNameHeadingAndText?: SmallHeadingAndTextStyles;
    customerGridItem?: GridItemProps;
    customerHeadingAndText?: SmallHeadingAndTextStyles;
    shipToAddressGridItem?: GridItemProps;
    shipToAddressHeadingAndText?: SmallHeadingAndTextStyles;
    requestedBy?: GridItemProps;
    productNameGridItem?: GridItemProps;
}

export const quoteSummaryCardStyles: QuoteSummaryCardStyles = {
    quoteSummaryCardGridItem: {
        width: 12,
        css: css`
            padding: 24px 0;
            color: #333333;
            font-size: 1rem;
            font-weight: 400;
        `,
    },
    container: {
        css: css`
            > div {
                flex-wrap: nowrap!important;   
            }
        `,
    },
    statusGridItem: {
        width: 3
    },
    quoteDetailsLinkGridItem: {
        width: 2,
        css: css`                   
            span {
                color: #006FF8!important;
                text-decoration: underline;
                font-size: 1rem;
                font-weight: 400;
            }
        `,

    },
    orderDateGridItem: {
        width: 2,
        css: css`         
            color: #333333;
            font-size: 1rem;
            font-weight: 400;           
        `,
    },
    requestedBy: { width: 3 },
    productNameGridItem: {
        width: 2,
        css: css`
            padding: 15px 15px 15px 7px;

            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`padding: 15px;`,
                    css`padding: 15px;`,
                    null,
                    null,
                    null
                ]);
            }}  
        `
    },
    statusHeadingAndText: {
        text: {
            color: "#CA4F00"
        }
    },
};

const QuoteSummaryCard: FC<Props> = ({ quote, session, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(quoteSummaryCardStyles, extendedStyles));

    const orderDateDisplay = getLocalizedDate({
        date: quote.orderDate!,
        language: session.language
    });
    const productName = quote.properties && quote.properties.quotedProduct;

    return (
        <GridItem {...styles.quoteSummaryCardGridItem} data-test-selector="recentQuotes">
            <GridContainer {...styles.container}>
                <GridItem {...styles.quoteDetailsLinkGridItem}>
                    <RfqQuoteDetailsPageTypeLink title={quote.quoteNumber} quoteId={quote.id} />
                </GridItem>
                <GridItem {...styles.productNameGridItem}>
                    <SmallHeadingAndText
                        heading=""
                        text={productName ?? ""}
                    />
                </GridItem>
                <GridItem {...styles.orderDateGridItem}>
                    <SmallHeadingAndText
                        heading=""
                        text={orderDateDisplay}
                        extendedStyles={styles.orderDateHeadingAndText}
                    />
                </GridItem>
                <GridItem {...styles.statusGridItem}>
                    <SmallHeadingAndText
                        heading=""
                        text={translatedStatus(quote.status)}
                        extendedStyles={quote.status === "QuoteRequested" ? styles.statusHeadingAndText : undefined}
                    />
                </GridItem>
                <GridItem {...styles.requestedBy}>
                    <SmallHeadingAndText
                        heading=""
                        text={quote.userName}
                        extendedStyles={styles.customerHeadingAndText}
                    />
                </GridItem>
            </GridContainer>
        </GridItem>
    );
};

export default QuoteSummaryCard;
